import { Button } from '@heroui/button';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { API_CATALOG } from '@/assets/data/api-details/apis-details';
import ApiDocumentation from '@/components/api-details/api-documentation';
import ApiEndpoints from '@/components/api-details/api-endpoints';
import LazyImage from '@/components/common/lazy-image';
import DefaultLayout from '@/layouts/default';
import { cn } from '@/lib/utils';
const menuItems = [
  { key: 'api-overview', label: 'API Overview' },
  { key: 'core-features-1', label: 'Core Features' },
  { key: 'workflow-overview', label: 'Workflow Overview' },
  { key: 'api-endpoints', label: 'API Endpoints' },
  { key: 'api-documentation', label: 'API Documentation' },
  { key: 'use-cases', label: 'Use Cases' },
  { key: 'target-users', label: 'Target Users' },
];
const ApiDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [activeMenu, setActiveMenu] = useState('api-overview');
  const isManualScrolling = useRef(false);
  const API_INFO = API_CATALOG?.[Number(id) - 1] || API_CATALOG[0];

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '-10% 0px -85% 0px',
      threshold: 0,
    };

    const handleIntersect = (entries: IntersectionObserverEntry[]) => {
      if (isManualScrolling.current) return;

      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveMenu(entry.target.id);
        }
      });
    };

    const observer = new IntersectionObserver(handleIntersect, observerOptions);

    menuItems.forEach((item) => {
      const element = document.getElementById(item.key);

      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, []);

  const scrollToSection = (id: string) => {
    isManualScrolling.current = true;
    setActiveMenu(id);
    const element = document.getElementById(id);

    if (element) {
      const headerOffset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition =
        elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }

    // Reset the manual scrolling flag after the smooth scroll finishes
    setTimeout(() => {
      isManualScrolling.current = false;
    }, 800);
  };

  const navigateContact = () => {
    navigate(`/contact?apiId=${id}`);
  };

  return (
    <DefaultLayout title="API Details">
      <section className="pt-[84px] pb-10" id="api-overview">
        <div className="container pt-20">
          <div className="mb-5 flex items-center gap-5 justify-between">
            <div className="flex items-center gap-5">
              <div className="size-[84px] rounded-md bg-gradient-to-r from-white/5 to-white/0 border border-white/10">
                <LazyImage
                  alt={API_INFO?.title as string}
                  className="rounded-md p-3"
                  src={API_INFO?.logo as string}
                />
              </div>
              <div>
                <h1 className="text-[28px] font-semibold font-lato">
                  {API_INFO.title}
                </h1>
                <p className="text-base text-white">{API_INFO.subtitle}</p>
              </div>
            </div>
            <Button
              color="primary"
              radius="full"
              size="lg"
              onPress={navigateContact}
            >
              Contact For Pricing
            </Button>
          </div>
          {API_INFO.description?.map((desc, index) =>
            desc === '' ? (
              <div key={index} className="h-5" />
            ) : (
              <p key={index} className="text-base mb-2">
                {desc}
              </p>
            ),
          )}
          {/* Add more API details here as needed */}
        </div>
        <div className="container pt-8">
          <div className="border-t border-t-white/20 grid grid-cols-1 md:grid-cols-4 pt-8 gap-10">
            <div className="md:col-span-1">
              <div className="border border-white/20 bg-gradient-to-r from-white/5 to-white/0 p-5 rounded-md sticky top-28">
                {menuItems.map((item) => (
                  <div key={item.key} className="mb-2.5">
                    <button
                      className={cn(
                        `transition-all text-base font-semibold font-lato bg-[#16171A] text-white h-[52px] px-5 w-full rounded-md text-left`,
                        activeMenu === item.key && 'bg-primary',
                      )}
                      onClick={() => scrollToSection(item.key)}
                    >
                      {item.label}
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Contents */}
            <div className="md:col-span-3 space-y-12">
              <div id="api-overview">
                <h1 className="text-[28px] font-semibold font-lato mb-5">
                  API Overview
                </h1>

                {API_INFO.overview?.map((overview, index) =>
                  overview === '' ? (
                    <div key={index} className="h-5" />
                  ) : (
                    <p key={index} className="text-base mb-2">
                      {overview}
                    </p>
                  ),
                )}
              </div>

              <div id="core-features-1">
                <h1 className="text-[28px] font-semibold font-lato mb-5">
                  Core Features
                </h1>
                <ul className="list-disc ml-4 space-y-5">
                  {API_INFO.features?.map((feature) => (
                    <li key={feature.title}>
                      <h6 className="text-base text-white font-medium">
                        {feature.title}
                      </h6>
                      <p className="text-sm text-white/80">
                        {feature.description}
                      </p>
                    </li>
                  ))}
                </ul>
              </div>

              {API_INFO.workflow && API_INFO.workflow.length > 0 && (
                <div id="workflow-overview">
                  <h1 className="text-[28px] font-semibold font-lato mb-5">
                    Workflow Overview
                  </h1>
                  <ul className="list-decimal ml-4 gap-x-5 gap-y-3 grid grid-cols-2">
                    {API_INFO.workflow.map((workflow) => (
                      <li key={workflow}>
                        <h6 className="text-base text-white font-medium">
                          {workflow}
                        </h6>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div id="api-endpoints">
                <h1 className="text-[28px] font-semibold font-lato mb-5">
                  API Endpoints
                </h1>
                {API_INFO.endpoints?.map((endpoint) => (
                  <div key={endpoint.category} className="mb-5 last:mb-0">
                    {API_INFO.endpoints?.length > 1 && (
                      <h2 className="text-[20px] font-semibold font-lato mb-5">
                        {endpoint.category}
                      </h2>
                    )}
                    <ApiEndpoints endpoints={endpoint.items} />
                  </div>
                ))}
              </div>

              <div id="api-documentation">
                <h1 className="text-[28px] font-semibold font-lato mb-5">
                  API Documentation
                </h1>
                {API_INFO.apiDocumentation?.map((apiDocumentation) => (
                  <div
                    key={apiDocumentation.title}
                    className="mb-5 last:mb-0 border border-white/10 rounded-md bg-gradient-to-r from-white/5 to-white/0"
                  >
                    <h2 className="text-[20px] font-semibold font-lato mb-5 p-4 bg-white/10 rounded-t-md">
                      {apiDocumentation.method}{' '}
                      <span className="text-lg font-normal">
                        {apiDocumentation.path}
                      </span>
                    </h2>
                    <ApiDocumentation apiDocumentation={apiDocumentation} />
                  </div>
                ))}
              </div>

              <div id="use-cases">
                <h1 className="text-[28px] font-semibold font-lato mb-5">
                  Use Cases
                </h1>
                <ul className="grid grid-cols-2 gap-3 ist-none">
                  {API_INFO.useCases?.map((useCase) => (
                    <li key={useCase.title} className="flex items-start gap-2">
                      <span className="">✓</span>
                      <div>
                        <h6 className="text-base text-white font-medium">
                          {useCase.title}
                        </h6>
                        <p className="text-sm text-white/80">
                          {useCase.description}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              <div id="target-users">
                <h1 className="text-[28px] font-semibold font-lato mb-5">
                  Target Users
                </h1>
                <ul className="list-disc ml-3 gap-3 grid grid-cols-2">
                  {API_INFO.targetUsers?.map((targetUser) => (
                    <li key={targetUser}>
                      <h6 className="text-base text-white font-medium">
                        {targetUser}
                      </h6>
                    </li>
                  ))}
                </ul>
              </div>
              <Button
                color="primary"
                radius="full"
                size="lg"
                onPress={navigateContact}
              >
                Get Demo
              </Button>
            </div>
          </div>
        </div>
      </section>
    </DefaultLayout>
  );
};

export default ApiDetails;
